import { apiService } from './api';

/** Matches API ContentTagTarget: 0 = block, 1 = lesson */
export type ContentTagTargetDto = 0 | 1;

export interface ContentTagDto {
  id: number;
  name: string;
  namePublished: string;
  namePublishedFa?: string;
  description?: string;
  descriptionPublished?: string;
  descriptionPublishedFa?: string;
  addPublicCard: boolean;
  color: string;
  icon?: string;
  backgroundImageUrl?: string;
  isGlobal: boolean;
  courseId?: number;
  createdAt: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  usageCount: number;
  lessonUsageCount?: number;
  /** 0 = block tag, 1 = lesson tag */
  target?: ContentTagTargetDto;
}

export interface PublicContentTagDto {
  id: number;
  namePublished: string;
  namePublishedFa?: string;
  descriptionPublished?: string;
  descriptionPublishedFa?: string;
  color: string;
  icon?: string;
  backgroundImageUrl?: string;
  /** 0 = block practice tag, 1 = lesson tag */
  target?: ContentTagTargetDto;
}

export interface CreateContentTagDto {
  name: string;
  namePublished: string;
  namePublishedFa?: string;
  description?: string;
  descriptionPublished?: string;
  descriptionPublishedFa?: string;
  addPublicCard: boolean;
  color: string;
  icon?: string;
  backgroundImageUrl?: string;
  isGlobal: boolean;
  courseId?: number;
  /** 0 = block tag, 1 = lesson tag (create only; cannot change after use) */
  target?: ContentTagTargetDto;
}

export interface UpdateContentTagDto {
  name?: string;
  namePublished?: string;
  namePublishedFa?: string;
  description?: string;
  descriptionPublished?: string;
  descriptionPublishedFa?: string;
  addPublicCard?: boolean;
  color?: string;
  icon?: string;
  backgroundImageUrl?: string;
  isGlobal?: boolean;
  courseId?: number;
}

export interface PublicContentBlockDto {
  id: number;
  lessonId: number;
  type: number;
  displayOrder: number;
  title?: string;
  textContent?: string;
  audioUrl?: string;
  audioFileName?: string;
  imageUrl?: string;
  videoUrl?: string;
  createdAt: string;
  updatedAt?: string;
  courseName: string;
  levelName: string;
  tags: PublicContentTagDto[];
}

/** Admin: block with full path (course/level/lesson/block) for tag tab */
export interface AdminBlockByTagDto {
  id: number;
  courseName: string;
  levelName: string;
  lessonTitle: string;
  lessonId: number;
  displayOrder: number;
  type: number;
  title?: string;
  /** Shared by blocks that form one learner lesson in this tag. */
  lessonGroupKey?: number | null;
  tagSortOrder?: number | null;
}

/** Admin: lesson with full path for tag tab */
export interface AdminLessonByTagDto {
  id: number;
  courseName: string;
  levelName: string;
  lessonTitle: string;
  levelId: number;
  displayOrder: number;
  status: number;
  tagSortOrder?: number | null;
}

export interface TagUsageDto {
  tagId: number;
  tagName: string;
  tagNamePublished: string;
  color: string;
  usageCount: number;
  percentage: number;
}

export interface TagDistributionDto {
  courseId?: number;
  courseName?: string;
  tagCount: number;
  taggedBlockCount: number;
}

export interface TagStatisticsDto {
  totalTags: number;
  globalTags: number;
  courseScopedTags: number;
  publicCardTags: number;
  privateTags: number;
  totalTaggedBlocks: number;
  blocksWithMultipleTags: number;
  blocksWithNoTags: number;
  topTagsByUsage: TagUsageDto[];
  tagDistributionByCourse: TagDistributionDto[];
}

class ContentTagService {
  private baseUrl = '/api/ContentTags';

  /**
   * Get all tags (admin)
   */
  async getAllTags(isGlobal?: boolean, courseId?: number, addPublicCard?: boolean, target?: ContentTagTargetDto): Promise<ContentTagDto[]> {
    const params = new URLSearchParams();
    if (isGlobal !== undefined) params.append('isGlobal', isGlobal.toString());
    if (courseId !== undefined) params.append('courseId', courseId.toString());
    if (addPublicCard !== undefined) params.append('addPublicCard', addPublicCard.toString());
    if (target !== undefined) params.append('target', String(target));

    const url = params.toString() ? `${this.baseUrl}?${params.toString()}` : this.baseUrl;
    // Admin list must reflect creates/updates immediately; apiService GET cache would serve stale data
    return apiService.get<ContentTagDto[]>(url, { cache: false });
  }

  /**
   * Get public tags (where addPublicCard = true)
   * Cache disabled to ensure fresh data on each load
   */
  async getPublicTags(): Promise<PublicContentTagDto[]> {
    return apiService.get<PublicContentTagDto[]>(`${this.baseUrl}/public`, { cache: false });
  }

  /**
   * Get tag by ID (admin)
   */
  async getTagById(id: number): Promise<ContentTagDto> {
    return apiService.get<ContentTagDto>(`${this.baseUrl}/${id}`);
  }

  /**
   * Create a new tag (admin)
   */
  async createTag(dto: CreateContentTagDto): Promise<ContentTagDto> {
    return apiService.post<ContentTagDto>(this.baseUrl, dto);
  }

  /**
   * Update an existing tag (admin)
   */
  async updateTag(id: number, dto: UpdateContentTagDto): Promise<ContentTagDto> {
    return apiService.put<ContentTagDto>(`${this.baseUrl}/${id}`, dto);
  }

  /**
   * Delete a tag (admin)
   */
  async deleteTag(id: number): Promise<void> {
    return apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Get tags for a content block (admin)
   */
  async getTagsForBlock(blockId: number): Promise<ContentTagDto[]> {
    // Lesson editor reads this after a tag is removed from the Tags tab. A cached copy would keep the tag on the block.
    return apiService.get<ContentTagDto[]>(`${this.baseUrl}/block/${blockId}`, { cache: false });
  }

  /**
   * Assign tags to a content block (admin)
   */
  async assignTagsToBlock(blockId: number, tagIds: number[]): Promise<void> {
    return apiService.post(`${this.baseUrl}/block/${blockId}/assign`, tagIds);
  }

  /**
   * Get published blocks by tag (public)
   */
  async getBlocksByTag(tagId: number): Promise<PublicContentBlockDto[]> {
    return apiService.get<PublicContentBlockDto[]>(`${this.baseUrl}/public/blocks/${tagId}`);
  }

  /**
   * Get all blocks for a tag with full path - admin (course/level/lesson/block)
   */
  async getBlocksByTagAdmin(tagId: number): Promise<AdminBlockByTagDto[]> {
    // Must stay fresh after merge, split, and removing a block from the tag.
    return apiService.get<AdminBlockByTagDto[]>(`${this.baseUrl}/admin/blocks/${tagId}`, { cache: false });
  }

  async mergeTagBlockLesson(tagId: number, blockIds: number[]): Promise<void> {
    return apiService.post(`${this.baseUrl}/${tagId}/block-lesson-groups`, { blockIds });
  }

  async splitTagBlockLesson(tagId: number, lessonGroupKey: number): Promise<void> {
    return apiService.delete(`${this.baseUrl}/${tagId}/block-lesson-groups/${lessonGroupKey}`);
  }

  async setTagSortOrder(
    tagId: number,
    orders: { blockOrders?: { id: number; sortOrder: number }[]; lessonOrders?: { id: number; sortOrder: number }[] }
  ): Promise<void> {
    return apiService.post(`${this.baseUrl}/${tagId}/sort-order`, {
      blockOrders: orders.blockOrders ?? [],
      lessonOrders: orders.lessonOrders ?? []
    });
  }

  async getLessonsByTagAdmin(tagId: number): Promise<AdminLessonByTagDto[]> {
    return apiService.get<AdminLessonByTagDto[]>(`${this.baseUrl}/admin/lessons/${tagId}`, { cache: false });
  }

  async getTagsForLesson(lessonId: number): Promise<ContentTagDto[]> {
    return apiService.get<ContentTagDto[]>(`${this.baseUrl}/lesson/${lessonId}`, { cache: false });
  }

  async assignTagsToLesson(lessonId: number, tagIds: number[]): Promise<void> {
    return apiService.post(`${this.baseUrl}/lesson/${lessonId}/assign`, tagIds);
  }

  /**
   * Get tag statistics (admin)
   */
  async getTagStatistics(): Promise<TagStatisticsDto> {
    return apiService.get<TagStatisticsDto>(`${this.baseUrl}/statistics`, { cache: false });
  }
}

export const contentTagService = new ContentTagService();

